import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
    Image,
    IconButton,
    useDisclosure,
    Flex,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {ChevronLeftIcon, ChevronRightIcon} from '../../icons'
import {useReviewPhotos} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import WebDrawer from '../../web-drawer'

/* Components */
import ReviewCard from './review-card'

const ExpandedImage = ({selectedReview, setSelectedReview, closeOtherModals, allReviews = []}) => {
    const styles = useMultiStyleConfig('WriteReview')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [track, setTrack] = useState(null)
    const triggerElementRef = useRef(null)
    const [activeReview, setActiveReview] = useState(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const [initialSlide, setInitialSlide] = useState(0)

    // All reviews photos
    const allPhotos = useReviewPhotos(allReviews)

    useEffect(() => {
        if (selectedReview?.review && Object.keys(selectedReview?.review).length > 0) {
            triggerElementRef.current = selectedReview?.triggerElement || document.activeElement
            const idx = allPhotos.findIndex(
                (p) => p.review?.Id === selectedReview.review.Id && p.index === selectedReview.index
            )
            const targetIndex = idx >= 0 ? idx : 0
            setInitialSlide(targetIndex)
            setActiveIndex(targetIndex)
            setActiveReview(selectedReview.review)
            handleOnOpen()
        }
    }, [selectedReview?.review, selectedReview?.index])

    const restoreFocusToTrigger = () => {
        const triggerElement = triggerElementRef.current
        if (typeof triggerElement?.focus !== 'function') {
            return
        }

        const focusTrigger = () => {
            triggerElement.focus({preventScroll: true})
        }

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(focusTrigger)
            return
        }

        focusTrigger()
    }

    const scrollToPhoto = useCallback(
        (index, behavior = 'smooth') => {
            if (!track) {
                return
            }

            const slide = track.children?.[index]
            const target = slide ? slide.offsetLeft : index * track.clientWidth

            if (typeof track.scrollTo === 'function') {
                track.scrollTo({left: target, behavior})
                return
            }

            track.scrollLeft = target
        },
        [track]
    )

    // Keep the review card in sync with whichever photo the track is snapped to
    const updateActivePhoto = useCallback(() => {
        if (!track || !track.clientWidth) {
            return
        }

        const index = Math.round(track.scrollLeft / track.clientWidth)
        const photo = allPhotos[index]
        if (!photo) {
            return
        }

        setActiveIndex(index)
        if (photo.review?.Id !== activeReview?.Id) {
            setActiveReview(photo.review)
        }
    }, [track, allPhotos, activeReview?.Id])

    useEffect(() => {
        if (!isOpen || !track) {
            return
        }

        track.addEventListener('scroll', updateActivePhoto, {passive: true})
        return () => track.removeEventListener('scroll', updateActivePhoto)
    }, [isOpen, track, updateActivePhoto])

    // Jump to the clicked photo without animation. The drawer portal can still be
    // unmeasured (clientWidth 0) on open, so wait for a real width before scrolling.
    useEffect(() => {
        if (!isOpen || !track) {
            return
        }

        const canUseRaf =
            typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        let frameId
        let attemptsLeft = 20

        const jumpToInitialPhoto = () => {
            if (!track.clientWidth && canUseRaf && attemptsLeft > 0) {
                attemptsLeft -= 1
                frameId = window.requestAnimationFrame(jumpToInitialPhoto)
                return
            }

            scrollToPhoto(initialSlide, 'auto')
        }

        jumpToInitialPhoto()

        return () => {
            if (frameId) {
                window.cancelAnimationFrame(frameId)
            }
        }
    }, [isOpen, track, initialSlide, scrollToPhoto])

    const handleOnClose = () => {
        onClose()
        setSelectedReview({})
        restoreFocusToTrigger()
    }

    const handleOnOpen = () => {
        closeOtherModals.closeAllReviewsModal()
        onOpen()
    }

    const handlePrevPhoto = () => scrollToPhoto(Math.max(activeIndex - 1, 0))
    const handleNextPhoto = () => scrollToPhoto(Math.min(activeIndex + 1, allPhotos.length - 1))

    return (
        <WebDrawer
            isOpen={isOpen}
            onClose={handleOnClose}
            title={
                <FormattedMessage
                    id="pdp_reviews.submitted.photos"
                    defaultMessage="Submitted Photos"
                />
            }
            titleTextStyle="webH3"
            size="full"
            noBodyStyle={true}
            /* Focus is restored manually with preventScroll, so the page does not jump on close */
            returnFocusOnClose={false}
        >
            <Box padding={{base: '24px 16px'}} sx={{scrollbarGutter: {base: 'auto', lg: 'stable'}}}>
                <Box position="relative">
                    <Box
                        ref={setTrack}
                        data-testid="expanded-image-track"
                        tabIndex={0}
                        role="group"
                        aria-label="Submitted photos"
                        sx={{
                            display: 'flex',
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': {display: 'none'},
                            '& > *': {
                                flexShrink: 0,
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                scrollSnapAlign: 'center'
                            }
                        }}
                    >
                        {allPhotos.map((photo) => (
                            <Box key={photo.key} data-testid="expanded-image-slide">
                                {/* 458px tall, width derived from the 2:3 ratio, so photos of any ratio fit uncropped */}
                                <Box
                                    h="458px"
                                    maxW="100%"
                                    bg="webBasic.100"
                                    sx={{aspectRatio: '2 / 3'}}
                                >
                                    <Image
                                        w="100%"
                                        h="100%"
                                        objectFit="contain"
                                        src={photo.url}
                                        alt="Review image"
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    {allPhotos.length > 1 && (
                        <Flex __css={styles.navContainer}>
                            <IconButton
                                aria-label="Previous"
                                variant={'link'}
                                __css={styles.navButton}
                                left={0}
                                className={'prev-photo'}
                                onClick={handlePrevPhoto}
                                isDisabled={activeIndex === 0}
                                sx={{
                                    '&:disabled': {
                                        opacity: '50%',
                                        background: 'white !important'
                                    }
                                }}
                                icon={<ChevronLeftIcon />}
                            />
                            <IconButton
                                aria-label="Next"
                                variant={'link'}
                                __css={styles.navButton}
                                right={0}
                                className={'next-photo'}
                                onClick={handleNextPhoto}
                                isDisabled={activeIndex === allPhotos.length - 1}
                                sx={{
                                    '&:disabled': {
                                        opacity: '50%',
                                        background: 'white !important'
                                    }
                                }}
                                icon={<ChevronRightIcon />}
                            />
                        </Flex>
                    )}
                </Box>
                <ReviewCard
                    key={'rc-expanded-' + activeReview?.Id}
                    review={activeReview}
                    configs={{
                        styles: {
                            container: {
                                border: 0,
                                py: {base: 6},
                                px: 0,
                                mb: {base: '56px'}
                            },
                            nickname: {
                                pb: {base: 2}
                            }
                        },
                        showMobileVersionToDesktop: true,
                        showDimensionStakedOnMobile: true,
                        showImages: false
                    }}
                />
            </Box>
        </WebDrawer>
    )
}

ExpandedImage.propTypes = {
    selectedReview: PropTypes.object,
    setSelectedReview: PropTypes.func,
    closeOtherModals: PropTypes.object,
    allReviews: PropTypes.array
}

export default ExpandedImage
